import React, { useState, useCallback } from "react";
import { Alert, CircularProgress, Popover, Snackbar, Tooltip } from "@mui/material";
import { Button, Icon, Modal, Note, PageFrame, PageHeader, Surface } from "@/components/ui";
import ErrorDisplay from "@/components/utils/ErrorDisplay";
import styles from "./OrderRecommendationList.module.css";
import { useOrderRecommendations } from "./hooks/useOrderRecommendations";
import { RecommendationFilters } from "./components/RecommendationFilters";
import { OrderRecommendationTable } from "./components/OrderRecommendationTable";
import QuickDecisionDialog from "./components/QuickDecisionDialog";
import ScopeGate from "@/components/utils/ScopeGate";
import { WRITE_ORDERS_AND_RECOMMENDATIONS } from "@/constants/scopeCodes";

/** Footer legend for the row and cell tints. Each swatch is painted with the same class as the
 *  cells it describes, so it cannot drift from them. */
const CELL_LEGEND: { swatchClass: string; label: string; title: string }[] = [
  { swatchClass: styles["legend-swatch-rec"], label: "Recommendation", title: "Analyst / IC / model recommendation for a company with no pending order yet" },
  { swatchClass: styles["target-add"], label: "Add below", title: "Analyst / IC has an 'Add below' recommendation" },
  { swatchClass: styles["rec-triggered-add"], label: "Add below triggered", title: "'Add below' and CMP has crossed the rec price with an actionable suggestion" },
  { swatchClass: styles["target-reduce"], label: "Reduce above", title: "Analyst / IC has a 'Reduce above' recommendation" },
  { swatchClass: styles["rec-triggered-reduce"], label: "Reduce above triggered", title: "'Reduce above' and CMP has crossed the rec price with an actionable suggestion" },
  { swatchClass: styles["cash-1"], label: "Can buy: 1–5", title: "1–5 PMS clients below target with cash to buy. Target = CIO target, or current holding % when no CIO decision. Hover the cell for details." },
  { swatchClass: styles["cash-2"], label: "Can buy: 6–20", title: "6–20 PMS clients below target with cash to buy." },
  { swatchClass: styles["cash-3"], label: "Can buy: 20+", title: "More than 20 PMS clients below target with cash to buy." },
];

const OrderRecommendationList: React.FC = () => {
  const {
    pendingOrders,
    loading,
    error,
    recStartDate,
    setRecStartDate,
    strategyFilter,
    setStrategyFilter,
    selectedCategories,
    setSelectedCategories,
    defaultCategories,
    searchTerm,
    setSearchTerm,
    isBulkEditing,
    bulkEditState,
    bulkEditSnapshot,
    bulkCreateState,
    bulkCreateSnapshot,
    startBulkEdit,
    cancelBulkEdit,
    handleBulkEditChange,
    handleBulkCreateChange,
    setConfirmDialog,
    confirmDialog,
    handleConfirmAction,
    closeConfirmDialog,
    snackbar,
    saveBulkEdits,
    saveErrors,
    groupedByCompany,
    filteredOmsRows,
    toggleCategory,
    closeSnackbar,
    sortCol,
    sortDir,
    toggleSort,
    orderTypeFilter,
    setOrderTypeFilter,
    priorityFilter,
    setPriorityFilter,
    decisionFilter,
    setDecisionFilter,
    lastOrderFilter,
    setLastOrderFilter,
    marketableFilter,
    setMarketableFilter,
    rowTypeFilter,
    setRowTypeFilter,
    filterLevel,
    setFilterLevel,
    countryTab,
    setCountryTab,
    exportOrdersCsv,
    riskByCompanyId,
    applyCioNote,
    companiesMissingLiquidity,
    fetchPendingOrders,
    setSnackbar,
    prevDayContinueCount,
    continuePrevDayOrders,
    todayContinueCount,
    markDomesticComplete,
    isMarkingComplete,
  } = useOrderRecommendations();

  const [quickDecisionOpen, setQuickDecisionOpen] = useState(false);
  const [continuePrevDayOpen, setContinuePrevDayOpen] = useState(false);
  const confirmContinuePrevDay = useCallback(() => {
    setContinuePrevDayOpen(false);
    void continuePrevDayOrders();
  }, [continuePrevDayOrders]);

  // Sign-off dialogs: the confirm, and the "nothing to send" note — the latter opens from
  // the pre-flight check below or from a notified:false answer.
  const [markCompleteOpen, setMarkCompleteOpen] = useState(false);
  const [noInstructionsOpen, setNoInstructionsOpen] = useState(false);
  const openMarkComplete = useCallback(() => {
    if (todayContinueCount === 0) {
      setNoInstructionsOpen(true);
      return;
    }
    setMarkCompleteOpen(true);
  }, [todayContinueCount]);
  const confirmMarkComplete = useCallback(async () => {
    setMarkCompleteOpen(false);
    // Only the race case opens the note; an outright failure is reported by the snackbar.
    if ((await markDomesticComplete()) === "nothing-to-send") setNoInstructionsOpen(true);
  }, [markDomesticComplete]);
  const handleQuickDecisionSaved = useCallback(
    (message: string) => {
      setSnackbar({ open: true, message, severity: "success" });
      void fetchPendingOrders();
    },
    [setSnackbar, fetchPendingOrders]
  );

  const [notesAnchor, setNotesAnchor] = useState<HTMLElement | null>(null);

  // Reset-warning banner: dismissible, stays dismissed for the browser-tab session.
  const [showResetWarning, setShowResetWarning] = useState(
    () => sessionStorage.getItem("orderRecResetWarningDismissed") !== "1"
  );
  const dismissResetWarning = () => {
    sessionStorage.setItem("orderRecResetWarningDismissed", "1");
    setShowResetWarning(false);
  };

  const isGlobalTab = countryTab === "global";
  // Domestic decisions are cleared by the 6 PM IST cron each working day. Global orders are
  // placed outside the system, so their decisions are never reset on a schedule — the
  // instruction stands until a trader marks the row Completed.
  const resetNotice = isGlobalTab
    ? "CIO decisions are not cleared automatically. An instruction stands until a trader marks the order Completed."
    : "CIO decisions are cleared automatically at 6:00 PM IST every working day. Limit price and execution settings are preserved.";
  const hasSaveErrors = saveErrors.createKeys.length > 0 || saveErrors.editIds.length > 0;
  // Only the first load swaps the table for a spinner; a save or refetch keeps it on screen
  // (dimmed) so the user keeps their scroll position.
  const initialLoading = loading && pendingOrders.length === 0;

  const headerActions = (
    <div className={styles["header-actions"]}>
      <Tooltip title="Page notes & legend" placement="top">
        <Button iconOnly aria-label="Page notes" onClick={e => setNotesAnchor(e.currentTarget)}>
          <Icon name="info" size={16} />
        </Button>
      </Tooltip>
      <Popover
        open={Boolean(notesAnchor)}
        anchorEl={notesAnchor}
        onClose={() => setNotesAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={styles.notes}>
          <div className={styles["notes-title"]}>Notes</div>
          <ul>
            <li>Last Order % is shown only for <em>Increase To Target</em> (buy) and <em>Sell To Target</em> orders. Holding % uses group-level AUM.</li>
            <li><strong>Continue</strong> — order is actively being worked; carried forward in today&apos;s session. <strong>Completed</strong> — order has been fully executed and will be removed on save.</li>
            <li><strong>Placed yesterday</strong> — an order was already placed for this company on the last working day.</li>
            <li><strong>Default sort order:</strong> today&apos;s decisions → new orders added today → previous day Continue → all others.</li>
            <li><strong>{isGlobalTab ? "Decision lifecycle" : "Daily reset"}:</strong> {resetNotice}{isGlobalTab ? " Completing the row records the final instruction and removes it from this list." : " The next session starts fresh."}</li>
          </ul>
        </div>
      </Popover>
      {!isBulkEditing && (
        <Tooltip title={`Download a CSV of the ${isGlobalTab ? "Global" : "Domestic"} pending orders`} placement="top">
          <Button icon={<Icon name="download" size={14} />} onClick={exportOrdersCsv}>
            Export CSV
          </Button>
        </Tooltip>
      )}
      <ScopeGate scope={WRITE_ORDERS_AND_RECOMMENDATIONS}>
        {isBulkEditing ? (
          <>
            <Button onClick={cancelBulkEdit} disabled={loading}>Cancel</Button>
            <Button variant="primary" onClick={saveBulkEdits} disabled={loading}>
              Save All Changes
            </Button>
          </>
        ) : (
          <>
            {!isGlobalTab && (
              <Tooltip
                title={
                  prevDayContinueCount > 0
                    ? "Mark yesterday's Continue orders as Continue again for today"
                    : "No previous day Continue orders to carry forward"
                }
                placement="top"
              >
                <span className={styles["tip-wrap"]}>
                  <Button
                    icon={<Icon name="refresh" size={14} />}
                    disabled={prevDayContinueCount === 0 || loading}
                    onClick={() => setContinuePrevDayOpen(true)}
                  >
                    Continue Prev
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* Domestic only — Global instructions already post on every save. */}
            {!isGlobalTab && (
              <Tooltip title="Send today's instructions to the trade desk" placement="top">
                <span className={styles["tip-wrap"]}>
                  <Button
                    icon={
                      isMarkingComplete
                        ? <CircularProgress size={14} color="inherit" />
                        : <Icon name="check" size={14} />
                    }
                    disabled={loading || isMarkingComplete}
                    onClick={openMarkComplete}
                  >
                    {isMarkingComplete ? "Notifying…" : "Notify Trade Desk"}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Record a CIO decision without going through the list" placement="top">
              <Button icon={<Icon name="plus" size={14} />} onClick={() => setQuickDecisionOpen(true)}>
                Quick Decision
              </Button>
            </Tooltip>
            <Button variant="primary" icon={<Icon name="edit" size={14} />} onClick={startBulkEdit}>
              Edit Orders
            </Button>
          </>
        )}
      </ScopeGate>
    </div>
  );

  const tableFooter = (
    <>
      <span className={styles["foot-count"]}>
        {groupedByCompany.length} {groupedByCompany.length === 1 ? "company" : "companies"} with orders
        {" · "}
        {filteredOmsRows.length} {filteredOmsRows.length === 1 ? "recommendation" : "recommendations"}
      </span>
      <div className={styles.legend}>
        {CELL_LEGEND.map(({ swatchClass, label, title }) => (
          <Tooltip key={label} placement="top" title={title}>
            <span className={styles["legend-item"]}>
              <span className={`${styles["legend-swatch"]} ${swatchClass}`} />
              {label}
            </span>
          </Tooltip>
        ))}
        <Tooltip placement="top" title="A target (Analyst / IC / CIO) exceeds the CORE max-permissible position. Hover the ⚠ on a cell to see the limit, and the company's current size & 21-day median traded volume.">
          <span className={styles["legend-item"]}>
            <span className={styles["legend-breach"]}>⚠</span>
            Risk-rule breach
          </span>
        </Tooltip>
        {hasSaveErrors && (
          <Tooltip placement="top" title="This row could not be saved — fix the highlighted entry (e.g. select Buy or Sell for an entered CIO position)">
            <span className={styles["legend-item"]}>
              <span className={`${styles["legend-swatch"]} ${styles["legend-swatch-error"]}`} />
              <span className={styles["legend-error-label"]}>Needs attention before saving</span>
            </span>
          </Tooltip>
        )}
      </div>
    </>
  );

  return (
    <div className={styles.page}>
      <PageFrame
        header={
          <PageHeader
            title="Orders & Recommendations"
            subtitle="Pending orders and analyst / IC recommendations awaiting a CIO decision."
            actions={headerActions}
          />
        }
      >
        {showResetWarning && (
          <Note tone="warning" onDismiss={dismissResetWarning}>{resetNotice}</Note>
        )}

        <RecommendationFilters
          countryTab={countryTab}
          setCountryTab={setCountryTab}
          recStartDate={recStartDate}
          setRecStartDate={setRecStartDate}
          strategyFilter={strategyFilter}
          setStrategyFilter={setStrategyFilter}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedCategories={selectedCategories}
          toggleCategory={toggleCategory}
          setSelectedCategories={setSelectedCategories}
          defaultCategories={defaultCategories}
          orderTypeFilter={orderTypeFilter}
          setOrderTypeFilter={setOrderTypeFilter}
          priorityFilter={priorityFilter}
          setPriorityFilter={setPriorityFilter}
          decisionFilter={decisionFilter}
          setDecisionFilter={setDecisionFilter}
          lastOrderFilter={lastOrderFilter}
          setLastOrderFilter={setLastOrderFilter}
          marketableFilter={marketableFilter}
          setMarketableFilter={setMarketableFilter}
          rowTypeFilter={rowTypeFilter}
          setRowTypeFilter={setRowTypeFilter}
          filterLevel={filterLevel}
          setFilterLevel={setFilterLevel}
        />

        {companiesMissingLiquidity.length > 0 && (
          <Note tone="warning">
            Liquidity data unavailable for {companiesMissingLiquidity.length}{" "}
            {companiesMissingLiquidity.length === 1 ? "company" : "companies"} — risk-rule checks skipped for:{" "}
            {companiesMissingLiquidity.join(", ")}
          </Note>
        )}

        {error && !loading ? (
          <ErrorDisplay title="Couldn't load orders" message={error} onRetry={() => void fetchPendingOrders()} />
        ) : (
          <Surface footer={initialLoading ? undefined : tableFooter}>
            <OrderRecommendationTable
              isGlobalTab={isGlobalTab}
              loading={initialLoading}
              refreshing={loading && !initialLoading}
              groupedByCompany={groupedByCompany}
              filteredOmsRows={filteredOmsRows}
              sortCol={sortCol}
              sortDir={sortDir}
              toggleSort={toggleSort}
              isBulkEditing={isBulkEditing}
              bulkEditState={bulkEditState}
              bulkEditSnapshot={bulkEditSnapshot}
              bulkCreateState={bulkCreateState}
              bulkCreateSnapshot={bulkCreateSnapshot}
              handleBulkEditChange={handleBulkEditChange}
              handleBulkCreateChange={handleBulkCreateChange}
              setConfirmDialog={setConfirmDialog}
              saveErrors={saveErrors}
              lastOrderFilter={lastOrderFilter}
              strategyFilter={strategyFilter}
              riskByCompanyId={riskByCompanyId}
              applyCioNote={applyCioNote}
            />
          </Surface>
        )}

        {/* Errors and warnings are bulleted lists of the rows that need fixing, so they need
            their line breaks kept and longer than 3s on screen to be read at all. */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={snackbar.severity === "success" ? 3000 : 12000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%", whiteSpace: "pre-line" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Modal
          open={confirmDialog.open}
          onClose={closeConfirmDialog}
          titleId="orec-confirm-title"
          maxWidth={444}
          title={confirmDialog.type === "delete" ? "Delete Pending Order?" : "Mark Order as Completed?"}
          footer={
            <>
              <Button onClick={closeConfirmDialog}>Cancel</Button>
              <Button
                variant={confirmDialog.type === "delete" ? "danger" : "success"}
                onClick={handleConfirmAction}
                autoFocus
              >
                {confirmDialog.type === "delete" ? "Delete" : "Mark Completed"}
              </Button>
            </>
          }
        >
          {confirmDialog.companyName && (
            <p className={`${styles["dialog-text"]} ${styles["dialog-company"]}`}>{confirmDialog.companyName}</p>
          )}
          <p className={styles["dialog-text"]}>
            {confirmDialog.type === "delete"
              ? "This will permanently remove the pending order. This cannot be undone."
              : "This will mark the order as completed and remove it from the active list."}
          </p>
        </Modal>

        <Modal
          open={continuePrevDayOpen}
          onClose={() => setContinuePrevDayOpen(false)}
          titleId="orec-continue-title"
          maxWidth={444}
          title="Continue Previous Day Orders?"
          footer={
            <>
              <Button onClick={() => setContinuePrevDayOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={confirmContinuePrevDay} autoFocus>
                Continue Orders
              </Button>
            </>
          }
        >
          <p className={styles["dialog-text"]}>
            This will mark {prevDayContinueCount}{" "}
            {prevDayContinueCount === 1 ? "Domestic order" : "Domestic orders"} that were
            set to <strong>Continue</strong> yesterday as Continue again for today. Limit price,
            CIO target and execution settings are not changed.
          </p>
        </Modal>

        <Modal
          open={markCompleteOpen}
          onClose={() => setMarkCompleteOpen(false)}
          titleId="orec-notify-title"
          maxWidth={444}
          title="Notify Trade Desk?"
          footer={
            <>
              <Button onClick={() => setMarkCompleteOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={confirmMarkComplete} autoFocus>
                Send Notification
              </Button>
            </>
          }
        >
          <p className={styles["dialog-text"]}>
            Posts a message asking the trade desk to work today&apos;s instructions. No order is changed.
          </p>
        </Modal>

        <Modal
          open={noInstructionsOpen}
          onClose={() => setNoInstructionsOpen(false)}
          titleId="orec-nothing-title"
          maxWidth={444}
          title="No Instructions to Send"
          footer={
            <Button onClick={() => setNoInstructionsOpen(false)} autoFocus>
              Close
            </Button>
          }
        >
          <p className={styles["dialog-text"]}>
            No Domestic order is set to <strong>Continue</strong> today, so nothing has
            been sent to the trade desk. If a decision was just added elsewhere, refresh
            the page and try again.
          </p>
        </Modal>

        <QuickDecisionDialog
          open={quickDecisionOpen}
          onClose={() => setQuickDecisionOpen(false)}
          onSaved={handleQuickDecisionSaved}
        />
      </PageFrame>
    </div>
  );
};

export default OrderRecommendationList;
